import { BehaviorSubject, Observable, Subject } from "rxjs";
import { RcLoggerService } from "./rcLoggerService";
import { RestartMessage } from "../messages/pocket-com";
import { ColorQuality, DeviceScale } from "app/modules/remote-control-ng/definitions";
import { RcContext } from "../context/rcContext";
import { Injectable } from "@angular/core";
import { RemoteControlStreamingContext } from "../context/deviceContext";
import { NgRemoteControlConnection } from "app/modules/remote-control/services/remote-control.connection";

@Injectable({ providedIn: 'root' })
export class RestartThrottling {

    private savedScale: number = DeviceScale.ScaleAuto;
    private savedMonitor: number = 0;

    // To be able to throttle the restart messages we sent to the agent we keep track of when the request was made.
    // If that time is bigger than maxRestartFrequency then we send it instantly, if it is not,
    // then the restart message will be delayed, and it  will be sent only after maxRestartFrequency has passed,
    // without no new restart requests in the middle.
    private restartRequestedOn: number = 0; //milliseconds timestamp
    private maxRestartFrequency = 500;

    private restartMessageDebounce: any;

    constructor(private mcrcLog: RcLoggerService) { }

    private readonly applyChangeDelay = 1000;
    private pendingMessage: RestartMessage = null;

    private throttleRestart(scale: number, bitDepth: number, imageQuality: number, context: RcContext) {
        const deviceContextHandler = context.deviceContextHandler;
        if (!deviceContextHandler) {
            return;
        }

        deviceContextHandler.updateWithRestartInfo(scale, bitDepth, imageQuality);

        // Only send the restart message if we already received the PC_INIT response
        // connection drops if we don't!
        if (deviceContextHandler.currentContext().device.loaded) {

            this.mcrcLog.info('[FST] sending: scale:' + scale + ' depth:' + bitDepth + ' quality:' + imageQuality);

            const message = new RestartMessage({ scale: scale, colorQuality: bitDepth, imageQuality: imageQuality });
            message.monitorId = context.multiScreen.requestMonitorId;
            // this._restartRequest.next(message);
            console.debug("------------createRestartRCMessage: require monitor: " + message.monitorId + "----------------");
            console.debug("CQ: " + message.colorQuality + ", scale: " + message.scale + ", IQ: " + message.imageQuality);
            context.session.sendMessage(message);
        } else {
            if (context.session.isConnected()) {
                // Wait for a while, and try again...
                this.mcrcLog.info('device not loaded, delaying restart message...');
                setTimeout(() => {
                    const deviceContext = context.deviceContextHandler.currentContext();
                    this.throttleRestart(deviceContext.client.restartInfo.scale, deviceContext.client.restartInfo.bitDepth, deviceContext.client.restartInfo.imageQuality, context);
                }, 500);
            } else {
                this.mcrcLog.info('device not connected, discarding restart message');
            }
        }

    }

    // TODO 2025.1: integrating with streaming RC (applyChange in remote-control.connection)
    // e.g. iff block by checking context.deviceContext.supportVideoStreaming
    send(scale: number, forceSend: boolean, context: RcContext) {
        if (scale === undefined || isNaN(scale)) {
            if (this.savedScale === DeviceScale.ScaleAuto) {
                const transformService = context.deviceScreenTransformService;
                if (transformService) {
                    scale = transformService.getScreenScale();
                }
            }
            else {
                scale = this.savedScale;
            }
        }
        else {
            // First switching monitor, but the scale does not change too much, set scale to 100
            if (context.multiScreen.requestMonitorId !== this.savedMonitor) {
                // console.log("First restart after switching, savedScale: " + this.savedScale);
                if (Math.abs(scale - this.savedScale) <= 5) {
                    this.mcrcLog.info('New scale ' + scale + ' is nearly same, set to 100');
                    scale = DeviceScale.Scale100;
                }
                this.savedMonitor = context.multiScreen.requestMonitorId;
                // console.log("savedMonitor set to " + this.savedMonitor);
            }
            this.savedScale = scale;
        }

        // Agent returns round up scales, so only request that same thing
        scale = Math.ceil(scale);

        // Limit the scale to 100, if further scaling is required, we'll do it in client side
        if (scale > DeviceScale.Scale100) {
            scale = DeviceScale.Scale100;
        }

        // The Agent limits the minimum resolution to 25%, so, to be consistent with it,
        // we wont request images lower than that.
        if (scale < DeviceScale.Scale25) {
            scale = DeviceScale.Scale25;
        }

        this.mcrcLog.debug('[FST] requesting scale: ' + scale + ' depth: ' + context.session.appUI.getDeviceBitDepth() + ' quality: ' + context.session.appUI.getDeviceImageQuality());

        const deviceContext = context.deviceContextHandler.currentContext();

        if (scale === deviceContext.client.restartInfo.scale &&
            context.session.appUI.getDeviceBitDepth() === deviceContext.client.restartInfo.bitDepth &&
            context.session.appUI.getDeviceImageQuality() === deviceContext.client.restartInfo.imageQuality && !forceSend) {
            this.mcrcLog.debug('[FST] discarding scale: ' + scale + ' depth: ' + context.session.appUI.getDeviceBitDepth() + ' image quality: ' + context.session.appUI.getDeviceImageQuality());
            return;
        }

        const currentRequestTime = Date.now();
        if (currentRequestTime > this.restartRequestedOn + this.maxRestartFrequency || forceSend) {
            // The previous restart (if any) was requested more than maxRestartFrequency ms ago,
            // we can just send this request instantly
            this.restartRequestedOn = currentRequestTime;
            this.throttleRestart(scale, context.session.appUI.getDeviceBitDepth(), context.session.appUI.getDeviceImageQuality(), context);
        }
        else {
            // A restart message was recently requested, we should use throttling
            if (this.restartMessageDebounce != null) {
                // remove the previous timer..
                clearTimeout(this.restartMessageDebounce);
            }
            this.restartMessageDebounce = setTimeout(() => {
                // This code will limit the restartMessages to a maximum of 1 every maxRestartFrequency milliseconds
                this.throttleRestart(scale, context.session.appUI.getDeviceBitDepth(), context.session.appUI.getDeviceImageQuality(), context);

                this.restartMessageDebounce = null;
            }, this.maxRestartFrequency);
        }
    }

    applyChange(forceApply: boolean, context: RcContext, scale?: number, quality?: number, colorDepth?: number): boolean {
        let deviceContext = <RemoteControlStreamingContext>context.deviceContext;
        let [changed, rescaled] = scale > 0
          ? this.updateScale(scale, context, devicePixelRatio ?? 1)
          : [false, undefined];

        if (quality === undefined) {
          quality = deviceContext.imageQuality; // updated by mcrc.appUI.setDeviceImageQuality()
        }
        if (quality > 0 && quality <= 100 && Math.abs(quality - deviceContext.quality) >= 5) {
          // 5% threshold
          deviceContext.quality = quality;
          changed = true;
        }

        if (forceApply || changed) {
            // Android agent will send a full screen when scale or quality changes, or flags is set to 1 only (without other bits)
            const restartOptions: Partial<RestartMessage> = {
                colorQuality: ColorQuality.ColorQuality32Bpp,
                // monitorId: context.session.appUI.getRequestMonitorId(),
                monitorId: 0,
                imageQuality: quality,
                scale: rescaled ?? scale,
                forceRestart: forceApply
            }
            const message = new RestartMessage(restartOptions);
            this.sendRestart(context, message);
        }

        return changed;
    }

  updateScale = (scale: number, context: RcContext, browserZoomLevel?: number): [boolean, number | undefined] => {
    let changed = false;
    const deviceContext = <RemoteControlStreamingContext>context.deviceContext;
    const maxScale = deviceContext.videoStreaming && deviceContext.maxScale > 0 ? deviceContext.maxScale : 100;
    if (scale > 0) {
      if (!browserZoomLevel) {
        // if the scale is a real scale (i.e. coming from the device), we only
        // adjust the $scale once, if the $scale is still 0

        // Zooming could be adjusted on all messages. However, during quick zooming, such messages
        // arrive in quick succession, some with the zoom scale initiated 2 zoom steps ago and that
        // doesn't match the zoom state (initiated before zoom was processed). As a result, quick
        // zooming in/out can result in erratic resizing. The following code is message scale
        // processing is disabled
        if (deviceContext.$scale > 0) {
          return [false, undefined];
        }
        browserZoomLevel = 1;
      }

      // Adjust scale based on the browser's zoom level + resolution
      const nScale = Math.min(Math.floor(scale * browserZoomLevel), maxScale);
      console.log(`Adjust scale: zoom = ${browserZoomLevel}, ${scale} => ${nScale}, $scale = ${deviceContext.$scale}`);
      scale = nScale;
    }

    if (
      scale > 0 &&
      scale <= maxScale &&
      (Math.abs(scale - deviceContext.$scale) > 5 || (scale === maxScale && deviceContext.$scale < maxScale))
    ) {
      // there is a 5% threshold
      //keep all comments in this iff statement and they will be useful for debugging later
      //console.log(`L0      $scale: ${this.$scale} prevScale: ${this.prevSaveScale}`);

      // Check if zooming in and not reaching the max scale
      if (deviceContext.$scale < scale && scale < maxScale) {
        this.savedScale = scale; // Save the current scale
      } else if (
        deviceContext.$scale > scale &&
        deviceContext.$scale == maxScale &&
        this.savedScale != DeviceScale.ScaleUnknown
      ) {
        // When zooming out from max scale, restore the last zoom-in step if it was smaller
        // (e.g., zoomed in: 62 → 72 → 82 → 92 → 100; zoom out: 100 → 92). Ignore ScaleAuto,
        // as it is not a real scale value
        if (this.savedScale < DeviceScale.ScaleAuto) {
          scale = this.savedScale; // Set the scale back to the saved scale
        }
        this.savedScale = DeviceScale.ScaleUnknown; // Reset prevSaveScale since we have used it
      }

      deviceContext.$scale = scale;
      changed = true;
      }
      return [changed, scale === maxScale ? maxScale : undefined];
    };

    public sendRestart = (context: RcContext, message?: RestartMessage): void => {
        // don't change scale when recording video
        const deviceContext = <RemoteControlStreamingContext>context.deviceContext;
        const streamingConnection = <NgRemoteControlConnection>deviceContext.remoteControlConnection;
        if (streamingConnection?.getVideoRecordingState()) {
            console.log('No scale change during recording');
            return;
        }

        // before connected, or before 1st screen is received when initial scale is non-zero, don't send PC_RESTART
        if (!context.session.isConnected() || deviceContext.lastChangeTimestamp < 0) {
            if (message) {
                this.pendingMessage = message;
            }
            return;
        }

        if (!message) {
            message = this.pendingMessage;
            if (!message) {
                return;
            }
        }

        const now = Date.now();
        this.pendingMessage = null;
        if (now - deviceContext.lastChangeTimestamp >= this.applyChangeDelay) {
            // this._restartRequest.next(message);
            context.session.sendMessage(message);
            deviceContext.lastChangeTimestamp = now;
        } else {
            this.pendingMessage = message;
            setTimeout(() => this.sendRestart(context), this.applyChangeDelay);
        }
    };
}
